import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { DataService } from "../../../services/api/data/data.service";
import { LoadingService } from "../../../services/loading/loading";
import { ThemeService } from "../../../services/theme/theme.service";
import { AuthService } from "../../../services/api/auth/auth.service";
import { ModalService } from "../../../services/modal/modal.service";
import { Subscription, finalize } from "rxjs";
import readXlsxFile from "read-excel-file";
import { Client } from "../../../models/client.model";
import { escapeCSV } from "../../utils/data-util";

type CsvColumn<T> = { header: string; get: (item: T) => string };

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: "GROWER" | "MANAGER" | "ADMIN" | "ENTITY";
  password?: string; // Only for creation
  latestloginat?: string;
  createdat: string;
  updatedat: string;
  phone?: string;
  isInternal: boolean;
  signup_client_id?: number;
  allowed_districts?: number[];
}

interface UserInvitation {
  id: string;
  name: string;
  email: string;
  invitation_code?: string;
  client_id: number;
  manager_id?: string;
  created_at: string;
  expires_at?: string;
  role?: "GROWER" | "MANAGER" | "ENTITY" | "ADMIN";
}

@Component({
  selector: "app-user-management",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./user-management.component.html",
  styleUrls: ["./user-management.component.scss"],
  host: {
    class: "flex flex-col h-full overflow-hidden",
  },
})
export class UserManagementComponent implements OnInit, OnDestroy {
  @ViewChild("editUserModal") editUserModal!: ElementRef;
  @ViewChild("deleteUserModal") deleteUserModal!: ElementRef;
  @ViewChild("sendInvitationModal") sendInvitationModal!: ElementRef;

  // Tab management
  activeTab: "invitations" | "users" = "users";

  // User Invitations data
  userInvitations: UserInvitation[] = [];
  filteredUserInvitations: UserInvitation[] = [];
  isLoadingInvitations = false;
  selectedInvitation: UserInvitation | null = null;

  // User Invitations search and filters
  invitationSearchTerm = "";
  invitationSortBy = "Created Date";
  invitationSortDirection: "asc" | "desc" = "desc";
  invitationSortOptions = ["Created Date", "Name", "Email", "Expires"];
  invitationRoleFilter: "" | "GROWER" | "MANAGER" = "";

  // Invitation Pagination properties
  invitationCurrentPage = 1;
  invitationItemsPerPage = 10;
  invitationTotalItems = 0;
  invitationTotalPages = 0;
  paginatedInvitations: UserInvitation[] = [];

  // Pagination properties
  currentPage = 1;
  itemsPerPage = 10;
  totalItems = 0;
  totalPages = 0;
  paginatedUsers: User[] = [];

  // Pagination options
  itemsPerPageOptions = [5, 10, 25, 50];

  Math = Math;

  // User Invitations modal states
  isSendInvitationModalOpen = false;
  isImportInvitationsModalOpen = false;
  isSendingInvitation = false;
  isDeleteInvitationModalOpen = false;

  // Send Invitation form data
  newInvitation: {
    name: string;
    email: string;
    userType: "GROWER" | "MANAGER";
    clientId: number | null;
  } = {
    name: "",
    email: "",
    userType: "GROWER",
    clientId: null,
  };

  // Send Invitation validation
  newInvitationErrors: any = {};

  // CSV Import for Invitations
  csvFile: File | null = null;
  csvData: any[] = [];
  csvErrors: string[] = [];
  isProcessingCsv = false;

  // Bulk invitation result modal state
  showBulkInvitationResult = false;
  bulkInvitationResult: {
    newInvitationsCount: number;
    districtAccessAddedCount: number;
    skipped: { email: string; reason: string }[];
    failuresCount: number;
    invitedRoleLabel: string;
  } | null = null;
  csvImportUserType: "GROWER" | "MANAGER" = "GROWER";
  csvImportClientId: number | null = null;

  // Modal title for the bulk-import result, honest about what actually happened.
  get bulkResultTitle(): string {
    const r = this.bulkInvitationResult;
    if (!r) return "Bulk Import Results";
    const anySucceeded =
      r.newInvitationsCount > 0 || r.districtAccessAddedCount > 0;
    if (anySucceeded && r.failuresCount === 0) {
      return "Success";
    }
    if (anySucceeded && r.failuresCount > 0) {
      return "Completed with warnings";
    }
    return "No action taken";
  }

  // CSV template for invitations (name and email only - client selected via dropdown)
  get csvRequiredColumns(): string[] {
    return ["name", "email"];
  }

  get csvTemplate(): string {
    return [
      "name,email",
      "John Doe,john.doe@example.com",
      "Jane Smith,jane.smith@example.com",
    ].join("\n");
  }

  // Users data (existing functionality)
  users: User[] = [];
  filteredUsers: User[] = [];
  selectedUser: User | null = null;
  isLoading = false;
  searchTerm = "";
  userRoleFilter: "" | "GROWER" | "MANAGER" = "";

  // Modal states
  isEditModalOpen = false;
  isDeleteModalOpen = false;

  // Form data - simplified editUser (removed role, isInternal)
  editUser = {
    id: "",
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    signup_client_id: undefined as number | undefined,
  };

  // Sort options
  sortBy:
    | "Created Date"
    | "First Name"
    | "Last Name"
    | "Email"
    | "Role"
    | "Last Login" = "Last Login";
  sortOptions = [
    "Created Date",
    "First Name",
    "Last Name",
    "Email",
    "Role",
    "Last Login",
  ];
  userSortDirection: "asc" | "desc" = "desc";

  // Form validation state
  editUserErrors: any = {};

  private themeSubscription?: Subscription;
  isDarkMode = false;

  // Internal user properties
  isInternalUser = false;
  isAdmin = false;
  clients: Client[] = [];
  selectedDistrictId: number | null = null;

  constructor(
    private dataService: DataService,
    private loadingService: LoadingService,
    private themeService: ThemeService,
    private authService: AuthService,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.fetchUserInvitations();
    this.fetchUsers();

    // Check if current user is internal admin
    const decodedToken = this.authService.getDecodedToken();
    this.isAdmin = decodedToken?.role === "ADMIN";
    this.isInternalUser = (decodedToken?.isInternal || false) && this.isAdmin;

    // Load clients list if user is admin (for district filter and manager invitations)
    if (this.isAdmin) {
      this.loadClients();
    }

    // Subscribe to theme changes
    this.themeSubscription = this.themeService.darkMode$.subscribe(
      (isDarkMode) => {
        this.isDarkMode = isDarkMode;
      },
    );
  }

  loadClients(): void {
    this.loadingService.start({
      token: "user-management-clients",
      message: "Loading clients",
    });
    this.dataService
      .getAllClients()
      .pipe(finalize(() => this.loadingService.stop("user-management-clients")))
      .subscribe({
        next: (clients) => {
          this.clients = clients;
        },
        error: (error) => {
          console.error("Error loading clients:", error);
        },
      });
  }

  ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  getUserDisplayName(user: Partial<User> | null | undefined): string {
    return [user?.first_name, user?.last_name].filter((n) => !!n).join(" ");
  }

  getDistrictNames(districtIds: number[] | null | undefined): string[] {
    if (!districtIds?.length) return [];
    return districtIds.map((id) => {
      const client = this.clients.find((c) => c.id === id);
      return client?.name ?? `District ${id}`;
    });
  }

  /** Find an existing pending invitation for a given email (case-insensitive). */
  private findPendingInvitationFor(
    email: string | null | undefined,
  ): UserInvitation | undefined {
    if (!email) return undefined;
    const normalized = email.trim().toLowerCase();
    return this.userInvitations.find(
      (inv) => inv.email.toLowerCase() === normalized,
    );
  }

  /**
   * Build the "Invitation already pending" error message used by both the
   * single-invite form and CSV row validation. `subject` is what appears after
   * "pending for" — e.g. "this email" for the form, or the actual email for CSV.
   */
  private pendingInvitationMessage(
    existing: UserInvitation,
    subject: string,
  ): string {
    const roleLabel =
      existing.role ?
        existing.role.charAt(0) + existing.role.slice(1).toLowerCase()
      : "invitation";
    const client = this.clients.find((c) => c.id === existing.client_id);
    const districtLabel = client?.name ?? `District ${existing.client_id}`;
    return (
      `Invitation already pending for ${subject} (${roleLabel}, ${districtLabel}). ` +
      `Delete that invitation first, or click Resend to refresh it.`
    );
  }

  // Tab Management
  setActiveTab(tab: "invitations" | "users"): void {
    this.activeTab = tab;
  }

  // User Invitations Methods
  fetchUserInvitations(): void {
    this.isLoadingInvitations = true;
    this.loadingService.start({
      token: "user-management-invitations",
      message: "Loading invitations",
    });
    this.dataService
      .getUserInvitations()
      .pipe(
        finalize(() => {
          this.loadingService.stop("user-management-invitations");
          this.isLoadingInvitations = false;
        }),
      )
      .subscribe({
        next: (invitations: any[]) => {
          this.userInvitations = (invitations || []).map((inv) => ({
            id: String(inv.id),
            name: inv.name,
            email: inv.email,
            invitation_code: inv.invitation_code,
            client_id: inv.client_id,
            manager_id: inv.manager_id,
            created_at: inv.created_at,
            expires_at: inv.expires_at,
            role: inv.role,
          }));
          this.applyInvitationFilters();
          this.isLoadingInvitations = false;
        },
        error: (error) => {
          console.error("Error fetching user invitations:", error);
          this.showNotification(
            "Failed to load invitations. Please try again.",
            false,
          );
          this.isLoadingInvitations = false;
        },
      });
  }

  applyInvitationFilters(): void {
    this.filteredUserInvitations = this.userInvitations.filter((invitation) => {
      const matchesSearch =
        invitation.name
          .toLowerCase()
          .includes(this.invitationSearchTerm.toLowerCase()) ||
        invitation.email
          .toLowerCase()
          .includes(this.invitationSearchTerm.toLowerCase());
      const matchesDistrict =
        !this.selectedDistrictId ||
        invitation.client_id === this.selectedDistrictId;
      const matchesRole =
        !this.invitationRoleFilter ||
        invitation.role === this.invitationRoleFilter;
      return matchesSearch && matchesDistrict && matchesRole;
    });

    this.sortInvitations();
  }

  sortInvitations(): void {
    this.filteredUserInvitations.sort((a, b) => {
      const dir = this.invitationSortDirection === "asc" ? 1 : -1;
      switch (this.invitationSortBy) {
        case "Name":
          return a.name.localeCompare(b.name) * dir;
        case "Email":
          return a.email.localeCompare(b.email) * dir;
        case "Role":
          return (a.role || "GROWER").localeCompare(b.role || "GROWER") * dir;
        case "Expires":
          return (
            (new Date(a.expires_at || "").getTime() -
              new Date(b.expires_at || "").getTime()) *
            dir
          );
        case "Created Date":
        default:
          return (
            (new Date(a.created_at).getTime() -
              new Date(b.created_at).getTime()) *
            dir
          );
      }
    });
    this.updateInvitationPagination();
  }

  // Invitation Pagination methods
  updateInvitationPagination(): void {
    this.invitationTotalItems = this.filteredUserInvitations.length;
    this.invitationTotalPages = Math.ceil(
      this.invitationTotalItems / this.invitationItemsPerPage,
    );

    const startIndex =
      (this.invitationCurrentPage - 1) * this.invitationItemsPerPage;
    const endIndex = startIndex + this.invitationItemsPerPage;
    this.paginatedInvitations = this.filteredUserInvitations.slice(
      startIndex,
      endIndex,
    );
  }

  goToInvitationPage(page: number): void {
    if (page >= 1 && page <= this.invitationTotalPages) {
      this.invitationCurrentPage = page;
      this.updateInvitationPagination();
    }
  }

  changeInvitationItemsPerPage(): void {
    this.invitationCurrentPage = 1;
    this.updateInvitationPagination();
  }

  getVisibleInvitationPages(): number[] {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, this.invitationCurrentPage - delta);
      i <=
      Math.min(
        this.invitationTotalPages - 1,
        this.invitationCurrentPage + delta,
      );
      i++
    ) {
      range.push(i);
    }

    if (this.invitationCurrentPage - delta > 2) {
      rangeWithDots.push(1, -1);
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (this.invitationCurrentPage + delta < this.invitationTotalPages - 1) {
      rangeWithDots.push(-1, this.invitationTotalPages);
    } else if (this.invitationTotalPages > 1) {
      rangeWithDots.push(this.invitationTotalPages);
    }

    return rangeWithDots;
  }

  onInvitationSearchChange(): void {
    this.invitationCurrentPage = 1;
    this.applyInvitationFilters();
  }

  onInvitationRoleFilterChange(): void {
    this.invitationCurrentPage = 1;
    this.applyInvitationFilters();
  }

  onUserRoleFilterChange(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  onInvitationSortChange(sort: string): void {
    this.invitationSortBy = sort;
    this.sortInvitations();
  }

  onInvitationHeaderSort(
    sort: "Created Date" | "Name" | "Email" | "Role" | "Expires",
  ): void {
    if (this.invitationSortBy === sort) {
      this.invitationSortDirection =
        this.invitationSortDirection === "asc" ? "desc" : "asc";
    } else {
      this.invitationSortBy = sort;
      this.invitationSortDirection = sort === "Created Date" ? "desc" : "asc";
    }
    this.sortInvitations();
  }

  validateNewInvitation(): boolean {
    this.newInvitationErrors = {};
    let isValid = true;

    // Name validation
    if (!this.newInvitation.name || this.newInvitation.name.trim().length < 2) {
      this.newInvitationErrors.name =
        "Name is required and must be at least 2 characters";
      isValid = false;
    }

    // Email validation
    if (
      !this.newInvitation.email ||
      !this.isValidEmail(this.newInvitation.email)
    ) {
      this.newInvitationErrors.email = "Valid email is required";
      isValid = false;
    } else {
      const existing = this.findPendingInvitationFor(this.newInvitation.email);
      if (existing) {
        this.newInvitationErrors.email = this.pendingInvitationMessage(
          existing,
          "this email",
        );
        isValid = false;
      }
    }
    // Existing users are handled gracefully by the backend — if the user
    // already has the target district we get outcome: 'skipped', otherwise
    // they're added to allowed_districts and notified. Don't pre-block here.

    // Client validation (required for all internal user invitations)
    if (this.isInternalUser && !this.newInvitation.clientId) {
      this.newInvitationErrors.clientId = "Please select a client";
      isValid = false;
    }

    return isValid;
  }

  openSendInvitationModal(): void {
    this.newInvitation = {
      name: "",
      email: "",
      userType: "GROWER",
      clientId: null,
    };
    this.newInvitationErrors = {};
    this.isSendInvitationModalOpen = true;
  }

  sendUserInvitation(): void {
    if (!this.validateNewInvitation() || this.isSendingInvitation) {
      return;
    }

    const name = this.newInvitation.name.trim();
    const email = this.newInvitation.email.trim().toLowerCase();
    const role = this.isInternalUser ? this.newInvitation.userType : undefined;
    // Send clientId for internal users (required for both Manager and Grower)
    const clientId =
      this.isInternalUser ? this.newInvitation.clientId : undefined;
    this.isSendingInvitation = true;

    this.loadingService.start({
      token: "user-management-send-invitation",
      message: "Sending invitation",
    });
    this.dataService
      .sendUserInvitation(name, email, role, clientId ?? undefined)
      .pipe(
        finalize(() => {
          this.loadingService.stop("user-management-send-invitation");
          this.isSendingInvitation = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          // Always refetch from the server so the UI reflects actual DB state
          // (backend may delete the prior invitation for this email, mutate
          // allowed_districts, etc. — we don't try to mirror those locally).
          this.fetchUserInvitations();
          this.fetchUsers();

          const outcome = response?.outcome;
          if (outcome === "new_invitation") {
            this.closeModals();
            this.showNotification(
              `Invitation sent to ${response?.email ?? email} successfully!`,
            );
          } else if (outcome === "district_added") {
            this.closeModals();
            this.showNotification(
              `${response.email} already had an account — district access added.`,
            );
          } else if (outcome === "skipped") {
            // The pending-invitation reason is already a full sentence with
            // its own call-to-action — don't double-prefix it.
            const isPendingMessage =
              typeof response?.reason === "string" &&
              response.reason.startsWith("Invitation already pending");
            const message =
              isPendingMessage ?
                response.reason
              : `${response.email} was skipped — ${response.reason}.`;
            this.showNotification(message, false);
          } else if (outcome === "silently_skipped") {
            this.showNotification(
              `${response.email} could not be invited.`,
              false,
            );
          } else {
            this.closeModals();
            this.showNotification("Invitation sent.", true);
          }
        },
        error: (error) => {
          console.error("Error sending invitation:", error);
          const message =
            error?.error?.message ||
            "Failed to send invitation. Please try again.";
          this.showNotification(message, false);
        },
      });
  }

  resendInvitation(invitation: UserInvitation): void {
    if (!invitation) return;

    // Entity invitations originate from a different backend flow (grower
    // sharing a field with a collaborator). Route the resend through the
    // dedicated Entity endpoint so the recipient receives the correct
    // "invited as Entity" email with the original grower's name.
    if (invitation.role === "ENTITY") {
      this.resendEntityInvitation(invitation);
      return;
    }

    // Admins have no signup_client_id fallback on the backend, so a resend
    // with a missing/zero client_id would 400 with "Unable to determine a
    // client...". Block it up-front with a clear message instead.
    if (this.isAdmin && !invitation.client_id) {
      this.showNotification(
        `Cannot resend this invitation — the district is missing on the record. Please delete it and create a new invitation.`,
        false,
      );
      return;
    }

    this.isSendingInvitation = true;
    this.loadingService.start({
      token: "user-management-resend-invitation",
      message: "Resending invitation",
    });
    this.dataService
      .sendUserInvitation(
        invitation.name,
        invitation.email,
        invitation.role,
        invitation.client_id || undefined,
        true, // force: explicit refresh, bypass the pending-invitation guard
      )
      .pipe(
        finalize(() => {
          this.loadingService.stop("user-management-resend-invitation");
          this.isSendingInvitation = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          // Refetch to match DB truth — resend deletes the old row and creates
          // a new one, so the old id in our local list is now stale.
          this.fetchUserInvitations();
          this.fetchUsers();

          const outcome = response?.outcome;
          if (outcome === "district_added") {
            this.showNotification(
              `${invitation.email} already has an account — district access added.`,
            );
          } else if (outcome === "skipped") {
            this.showNotification(
              `Could not resend — ${response.reason}.`,
              false,
            );
          } else {
            this.showNotification(
              `Invitation resent to ${invitation.email} successfully!`,
            );
          }
        },
        error: (error) => {
          console.error("Error resending invitation:", error);
          const message =
            error?.error?.message ||
            "Failed to resend invitation. Please try again.";
          this.showNotification(message, false);
        },
      });
  }

  private resendEntityInvitation(invitation: UserInvitation): void {
    // Entity invitations store the inviting grower's ID in manager_id
    // (overloaded column). Without it we can't regenerate the email with
    // the correct "invited by" context.
    if (!invitation.manager_id) {
      this.showNotification(
        "Cannot resend this Entity invitation — the grower link is missing.",
        false,
      );
      return;
    }

    this.isSendingInvitation = true;
    this.loadingService.start({
      token: "user-management-resend-invitation",
      message: "Resending invitation",
    });
    this.dataService
      .resendEntityInvitation(
        invitation.name,
        invitation.email,
        invitation.manager_id,
        invitation.client_id || undefined,
      )
      .pipe(
        finalize(() => {
          this.loadingService.stop("user-management-resend-invitation");
          this.isSendingInvitation = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          if (response?.success === false) {
            this.showNotification(
              `Failed to resend Entity invitation to ${invitation.email}.`,
              false,
            );
            return;
          }
          // Refresh the list to pick up the new expires_at from the server
          this.fetchUserInvitations();
          this.showNotification(
            `Entity invitation resent to ${invitation.email} successfully!`,
          );
        },
        error: (error) => {
          console.error("Error resending entity invitation:", error);
          const message =
            error?.error?.message ||
            "Failed to resend invitation. Please try again.";
          this.showNotification(message, false);
        },
      });
  }

  deleteInvitation(invitation: UserInvitation): void {
    this.loadingService.start({
      token: "user-management-delete-invitation",
      message: "Deleting invitation",
    });
    this.dataService
      .deleteUserInvitation(invitation.id)
      .pipe(
        finalize(() =>
          this.loadingService.stop("user-management-delete-invitation"),
        ),
      )
      .subscribe({
        next: () => {
          this.fetchUserInvitations();
          this.showNotification(
            `Invitation for ${invitation.email} deleted successfully!`,
          );
          this.isDeleteInvitationModalOpen = false;
          this.selectedInvitation = null;
        },
        error: (error) => {
          console.error("Error deleting invitation:", error);
          this.showNotification(
            "Failed to delete invitation. Please try again.",
            false,
          );
          this.isDeleteInvitationModalOpen = false;
        },
      });
  }

  openDeleteInvitationModal(invitation: UserInvitation): void {
    this.selectedInvitation = invitation;
    this.isDeleteInvitationModalOpen = true;
  }

  confirmDeleteInvitation(): void {
    if (this.selectedInvitation) {
      this.deleteInvitation(this.selectedInvitation);
    }
  }

  cancelDeleteInvitation(): void {
    this.isDeleteInvitationModalOpen = false;
    this.selectedInvitation = null;
  }

  // CSV Import Methods for Invitations
  openImportInvitationsModal(): void {
    this.csvFile = null;
    this.csvData = [];
    this.csvErrors = [];
    this.csvImportUserType = "GROWER";
    this.csvImportClientId = null;
    this.isImportInvitationsModalOpen = true;
  }

  onFileSelect(event: any): void {
    const file = event.target.files[0];
    if (file) {
      if (
        file.type === "text/csv" ||
        file.name.endsWith(".csv") ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".xlsx")
      ) {
        this.csvFile = file;
        this.csvErrors = [];
      } else {
        this.csvErrors = ["Please select a valid CSV or Excel file"];
        this.csvFile = null;
      }
    }
  }

  processCsvFile(): void {
    if (!this.csvFile) return;

    this.isProcessingCsv = true;
    this.csvErrors = [];

    const isExcel = this.csvFile.name.endsWith(".xlsx");

    if (isExcel) {
      this.processExcelFile(this.csvFile);
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e: any) => {
      try {
        const csv: string = e.target.result;
        this.parseCsvData(csv);
      } catch (error) {
        this.csvErrors = [
          "Error reading file. Please ensure it's a valid CSV or Excel file.",
        ];
        this.isProcessingCsv = false;
      }
    };

    reader.onerror = () => {
      this.csvErrors = [
        "Unable to read the file. Please close it if it is open elsewhere and try again.",
      ];
      this.isProcessingCsv = false;
    };

    reader.readAsText(this.csvFile);
  }

  async processExcelFile(file: File): Promise<void> {
    try {
      const csv = await this.convertExcelToCsv(file);
      this.parseCsvData(csv);
    } catch (error) {
      this.csvErrors = [
        "Error reading file. Please ensure it's a valid CSV or Excel file.",
      ];
      this.isProcessingCsv = false;
    }
  }

  async convertExcelToCsv(file: File): Promise<string> {
    const sheetRows = await readXlsxFile(file);
    if (!sheetRows.length) {
      throw new Error("Workbook does not contain any worksheets");
    }

    return sheetRows
      .map((row: unknown[]) =>
        row
          .map((value: unknown) => this.formatExcelCellForCsv(value))
          .join(","),
      )
      .join("\n");
  }

  formatExcelCellForCsv(value: unknown): string {
    let text = "";

    if (value === null || value === undefined) {
      text = "";
    } else if (typeof value === "object" && value !== null && "text" in value) {
      text = String((value as { text?: unknown }).text ?? "");
    } else {
      text = String(value);
    }

    const normalized = text.replace(/\r?\n/g, " ").trim();
    const escaped = normalized.replace(/"/g, '""');
    return /[",]/.test(escaped) ? `"${escaped}"` : escaped;
  }

  parseCsvLine(line: string, delimiter: string): string[] {
    const fields: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (inQuotes) {
        if (char === '"') {
          if (i + 1 < line.length && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === delimiter) {
          fields.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
    }

    fields.push(current.trim());
    return fields;
  }

  parseCsvData(csv: string): void {
    const lines = csv.split("\n").filter((line) => line.trim().length > 0);

    if (lines.length < 2) {
      this.csvErrors = [
        "CSV file must contain headers and at least one data row",
      ];
      this.isProcessingCsv = false;
      return;
    }

    // Detect delimiter (comma or tab)
    const firstLine = lines[0];
    const delimiter = firstLine.includes("\t") ? "\t" : ",";

    const headers = this.parseCsvLine(firstLine, delimiter).map((h) =>
      h.toLowerCase(),
    );

    const dataRows = lines.slice(1);

    // Validate required columns
    const missingColumns = this.csvRequiredColumns.filter(
      (col) => !headers.includes(col.toLowerCase()),
    );
    if (missingColumns.length > 0) {
      this.csvErrors = [
        `Missing required columns: ${missingColumns.join(", ")}`,
      ];
      this.isProcessingCsv = false;
      return;
    }

    // Parse data rows
    this.csvData = [];
    const errors: string[] = [];
    // Track emails we've already accepted so duplicates (including case
    // variants like John.Doe@Example.COM vs john.doe@example.com) get
    // flagged at parse time instead of wasting a backend + SES round-trip.
    const seenEmailRows = new Map<string, number>();

    dataRows.forEach((row, index) => {
      if (!row.trim()) return; // Skip empty rows

      const values = this.parseCsvLine(row, delimiter);

      if (values.length !== headers.length) {
        errors.push(
          `Row ${index + 2}: Column count mismatch (expected ${headers.length}, got ${
            values.length
          })`,
        );
        return;
      }

      const invitationData: any = {};
      headers.forEach((header, i) => {
        invitationData[header] = values[i] || "";
      });

      // Validate invitation data
      const validationErrors = this.validateInvitationData(
        invitationData,
        index + 2,
      );
      if (validationErrors.length > 0) {
        errors.push(...validationErrors);
        return;
      }

      // Dedupe by normalized email (trim + lowercase) across the whole CSV
      const normalizedEmail = (invitationData.email ?? "").trim().toLowerCase();
      if (seenEmailRows.has(normalizedEmail)) {
        const firstRow = seenEmailRows.get(normalizedEmail);
        errors.push(
          `Row ${index + 2}: Duplicate of row ${firstRow} (${normalizedEmail}) — remove one before importing.`,
        );
        return;
      }
      seenEmailRows.set(normalizedEmail, index + 2);

      // Transform data to match our interface
      const invitation = this.transformCsvInvitation(invitationData);
      this.csvData.push(invitation);
    });

    this.csvErrors = errors;
    this.isProcessingCsv = false;
  }

  validateInvitationData(invitationData: any, rowNumber: number): string[] {
    const errors: string[] = [];

    // Name validation
    if (!invitationData.name || invitationData.name.trim().length < 2) {
      errors.push(
        `Row ${rowNumber}: Name is required and must be at least 2 characters (got: "${invitationData.name}")`,
      );
    }

    // Email validation
    if (!invitationData.email || !this.isValidEmail(invitationData.email)) {
      errors.push(
        `Row ${rowNumber}: Valid email is required (got: "${invitationData.email}")`,
      );
    }

    // Note: existing users, pending invitations, and role conflicts are
    // handled per-row by the backend (new_invitation / district_added /
    // skipped with a reason). Those cases surface in the post-import result
    // modal alongside "already a Manager", "already has access", etc.
    // Only format/required-field and in-CSV duplicate checks happen here.

    return errors;
  }

  transformCsvInvitation(invitationData: any): any {
    return {
      name: invitationData.name.trim(),
      email: invitationData.email.toLowerCase().trim(),
    };
  }

  importInvitations(): void {
    if (this.csvData.length === 0 || this.csvErrors.length > 0) {
      return;
    }

    // Validate client selection for internal users
    if (this.isInternalUser && !this.csvImportClientId) {
      this.csvErrors = ["Please select a client before importing"];
      return;
    }

    this.isProcessingCsv = true;
    this.csvErrors = [];

    const role = this.isInternalUser ? this.csvImportUserType : undefined;
    const clientId = this.isInternalUser ? this.csvImportClientId : undefined;

    this.loadingService.start({
      token: "user-management-bulk-invitations",
      message: "Sending bulk invitations",
    });
    this.dataService
      .bulkSendUserInvitations(this.csvData, role, clientId ?? undefined)
      .pipe(
        finalize(() => {
          this.loadingService.stop("user-management-bulk-invitations");
          this.isProcessingCsv = false;
        }),
      )
      .subscribe({
        next: (result) => {
          const newInvitationsCount: number =
            typeof result?.newInvitationsCount === "number" ?
              result.newInvitationsCount
            : Array.isArray(result?.newInvitations) ?
              result.newInvitations.length
            : 0;
          const districtAccessAddedCount: number =
            typeof result?.districtAccessAddedCount === "number" ?
              result.districtAccessAddedCount
            : 0;
          const skipped: { email: string; reason: string }[] =
            Array.isArray(result?.skipped) ? result.skipped : [];
          const failures =
            Array.isArray(result?.failures) ? result.failures : [];

          // Always refetch invitations + users to match DB truth — skip the
          // local mirror/dedupe dance entirely.
          this.fetchUserInvitations();
          if (districtAccessAddedCount > 0) {
            this.fetchUsers();
          }

          if (failures.length > 0) {
            this.csvErrors = failures.map((failure: any) => {
              const identifier =
                failure?.email || failure?.name || "Unknown entry";
              const message = failure?.message || "Failed to send invitation.";
              return `${identifier}: ${message}`;
            });
          }

          // Build categorized result for the success modal.
          // - invitedRoleLabel = what the admin chose for the CSV (used for
          //   "New X Invited" — accurate for brand-new invitations).
          // - District-added rows are always existing Growers gaining a new
          //   district, so that line is labelled independently in the template.
          const invitedRoleLabel =
            this.csvImportUserType === "MANAGER" ? "Manager" : "Grower";
          this.bulkInvitationResult = {
            newInvitationsCount,
            districtAccessAddedCount,
            skipped,
            failuresCount: failures.length,
            invitedRoleLabel,
          };
          this.showBulkInvitationResult = true;

          if (failures.length === 0) {
            this.csvData = [];
            this.csvFile = null;
          }

          this.isProcessingCsv = false;
        },
        error: (error) => {
          const message =
            error?.error?.message ||
            "Failed to send invitations. Please check your data and try again.";
          this.csvErrors = [message];
          this.isProcessingCsv = false;
        },
      });
  }

  downloadTemplate(): void {
    const blob = new Blob([this.csvTemplate], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "invitation_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  }

  convertToCsv<T>(columns: CsvColumn<T>[], items: T[]): string {
    const headers = columns.map((c) => c.header).join(",");
    const rows = items.map((item) =>
      columns
        .map((c) => c.get(item))
        .map(escapeCSV)
        .join(","),
    );
    return [headers, ...rows].join("\n");
  }

  downloadFilteredList(): void {
    let csvData: string;
    let filename: string;

    if (this.activeTab === "users") {
      const columns: CsvColumn<User>[] = [
        { header: "First Name", get: (u) => u.first_name || "" },
        { header: "Last Name", get: (u) => u.last_name || "" },
        { header: "Email", get: (u) => u.email },
        { header: "Role", get: (u) => u.role },
        {
          header: "Last Login",
          get: (u) =>
            u.latestloginat ? this.formatDateTime(u.latestloginat) : "",
        },
        { header: "Created", get: (u) => this.formatDateTime(u.createdat) },
        {
          header: "Allowed Districts",
          get: (u) => this.getDistrictNames(u.allowed_districts).join("; "),
        },
      ];
      csvData = this.convertToCsv(columns, this.filteredUsers);
      filename = "users_filtered.csv";
    } else {
      const columns: CsvColumn<UserInvitation>[] = [
        { header: "Name", get: (inv) => inv.name },
        { header: "Email", get: (inv) => inv.email },
        { header: "Role", get: (inv) => inv.role || "GROWER" },
        {
          header: "Sent Date",
          get: (inv) => this.formatDateTime(inv.created_at),
        },
        {
          header: "Expires",
          get: (inv) =>
            inv.expires_at ? this.formatDateTime(inv.expires_at) : "",
        },
      ];
      csvData = this.convertToCsv(columns, this.filteredUserInvitations);
      filename = "invitations_filtered.csv";
    }

    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    window.URL.revokeObjectURL(url);
    anchor.remove();
  }

  trackByInvitationId(index: number, invitation: UserInvitation): string {
    return invitation.id;
  }

  // Existing Users Methods (simplified)
  fetchUsers(): void {
    this.isLoading = true;

    this.loadingService.start({
      token: "user-management-users",
      message: "Loading users",
    });
    this.dataService
      .getUsers()
      .pipe(
        finalize(() => {
          this.loadingService.stop("user-management-users");
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (users: any[]) => {
          // Map the backend response to match your frontend interface
          this.users = users.map((user) => ({
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            role: user.role,
            createdat: user.createdat,
            updatedat: user.updatedat,
            isInternal: user.isInternal,
            phone: user.phone,
            latestloginat: user.latestloginat,
            signup_client_id: user.signup_client_id,
            allowed_districts: user.allowed_districts,
          }));
          this.applyFilters();
        },
        error: (error) => {
          console.error("Error fetching users:", error);
          this.showNotification(
            "Failed to load users. Please try again.",
            false,
          );
        },
      });
  }

  applyFilters(): void {
    this.filteredUsers = this.users.filter((user) => {
      const fullName = [user.first_name, user.last_name]
        .filter(Boolean)
        .join(" ");
      const search = this.searchTerm.toLowerCase();
      const matchesSearch =
        fullName.toLowerCase().includes(search) ||
        user.email.toLowerCase().includes(search);
      const matchesDistrict =
        !this.selectedDistrictId ||
        (user.allowed_districts ?? []).includes(this.selectedDistrictId);
      const matchesRole =
        !this.userRoleFilter || user.role === this.userRoleFilter;

      return matchesSearch && matchesDistrict && matchesRole;
    });

    this.sortUsers();
    this.currentPage = 1; // Reset to first page
    this.updatePagination();
  }

  sortUsers(): void {
    this.filteredUsers.sort((a, b) => {
      const dir = this.userSortDirection === "asc" ? 1 : -1;
      switch (this.sortBy) {
        case "First Name":
          return (a.first_name || "").localeCompare(b.first_name || "") * dir;
        case "Last Name":
          return (a.last_name || "").localeCompare(b.last_name || "") * dir;
        case "Email":
          return a.email.localeCompare(b.email) * dir;
        case "Role":
          return (a.role || "").localeCompare(b.role || "") * dir;
        case "Last Login": {
          const aTime =
            a.latestloginat ? new Date(a.latestloginat).getTime() : 0;
          const bTime =
            b.latestloginat ? new Date(b.latestloginat).getTime() : 0;
          return (aTime - bTime) * dir;
        }
        case "Created Date":
        default:
          return (
            (new Date(a.createdat).getTime() -
              new Date(b.createdat).getTime()) *
            dir
          );
      }
    });
    this.updatePagination(); // ADD THIS LINE
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  onDistrictChange(): void {
    this.applyFilters();
    this.applyInvitationFilters();
  }

  onUserHeaderSort(
    sort:
      | "Created Date"
      | "First Name"
      | "Last Name"
      | "Email"
      | "Role"
      | "Last Login",
  ): void {
    if (this.sortBy === sort) {
      this.userSortDirection =
        this.userSortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortBy = sort;
      this.userSortDirection =
        sort === "Created Date" || sort === "Last Login" ? "desc" : "asc";
    }
    this.sortUsers();
  }

  getInvitationSortIcon(
    key: "Created Date" | "Name" | "Email" | "Role" | "Expires",
  ): "up" | "down" | "neutral" {
    if (this.invitationSortBy !== key) return "neutral";
    return this.invitationSortDirection === "asc" ? "up" : "down";
  }

  getUserSortIcon(
    key:
      | "Created Date"
      | "First Name"
      | "Last Name"
      | "Email"
      | "Role"
      | "Last Login",
  ): "up" | "down" | "neutral" {
    if (this.sortBy !== key) return "neutral";
    return this.userSortDirection === "asc" ? "up" : "down";
  }

  validateEditUser(): boolean {
    this.editUserErrors = {};
    let isValid = true;

    // First name validation
    if (
      !this.editUser.first_name ||
      this.editUser.first_name.trim().length < 2
    ) {
      this.editUserErrors.first_name =
        "First name is required and must be at least 2 characters";
      isValid = false;
    }

    // Last name validation
    if (!this.editUser.last_name || this.editUser.last_name.trim().length < 2) {
      this.editUserErrors.last_name =
        "Last name is required and must be at least 2 characters";
      isValid = false;
    }

    // Email validation
    if (!this.editUser.email || !this.isValidEmail(this.editUser.email)) {
      this.editUserErrors.email = "Valid email is required";
      isValid = false;
    } else if (
      this.users.some(
        (u) =>
          u.id !== this.editUser.id &&
          u.email.toLowerCase() === this.editUser.email.toLowerCase(),
      )
    ) {
      this.editUserErrors.email = "Email already exists";
      isValid = false;
    }

    // Phone validation (if provided)
    if (
      this.editUser.phone &&
      !/^\d{10}$/.test(this.editUser.phone.replace(/\D/g, ""))
    ) {
      this.editUserErrors.phone = "Phone must be exactly 10 digits";
      isValid = false;
    }

    return isValid;
  }

  openEditModal(user: User): void {
    this.editUser = {
      id: user.id,
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      email: user.email,
      phone: user.phone || "",
      signup_client_id: user.signup_client_id,
    };
    this.isEditModalOpen = true;
  }

  openDeleteModal(user: User): void {
    this.selectedUser = user;
    this.isDeleteModalOpen = true;
  }

  closeModals(): void {
    this.isEditModalOpen = false;
    this.isDeleteModalOpen = false;
    this.isDeleteInvitationModalOpen = false;
    this.isSendInvitationModalOpen = false;
    this.isImportInvitationsModalOpen = false;
    this.isSendingInvitation = false;
    this.selectedUser = null;
    this.selectedInvitation = null;
    this.csvFile = null;
    this.csvData = [];
    this.csvErrors = [];
  }

  updateUser(): void {
    if (!this.validateEditUser()) {
      return;
    }

    this.dataService
      .updateUser(this.editUser.id, {
        first_name: this.editUser.first_name,
        last_name: this.editUser.last_name,
        email: this.editUser.email,
        phone: this.editUser.phone,
        signup_client_id: this.editUser.signup_client_id,
      })
      .subscribe({
        next: () => {
          this.fetchUsers(); // Refresh the list
          this.closeModals();
          this.showNotification(
            `User "${this.editUser.first_name} ${this.editUser.last_name}" updated successfully!`,
          );
        },
        error: (error) => {
          console.error("Error updating user:", error);
          this.showNotification(
            "Failed to update user. Please try again.",
            false,
          );
        },
      });
  }

  deleteUser(): void {
    if (!this.selectedUser) return;

    // Temporarily disable backend delete until constraints are handled.
    // this.dataService.deleteUser(this.selectedUser.id).subscribe({
    //   next: () => {
    //     this.fetchUsers(); // Refresh the list
    //     this.closeModals();
    //     this.showNotification(`User "${this.selectedUser!.username}" deleted successfully!`);
    //   },
    //   error: (error) => {
    //     console.error('Error deleting user:', error);
    //     this.showNotification('Failed to delete user. Please try again.', false);
    //   }
    // });

    this.showNotification("User delete is temporarily disabled.", false);
    this.closeModals();
  }

  // Common Methods
  showNotification(message: string, isSuccess: boolean = true): void {
    this.modalService.openDialogAsync({
      title: isSuccess ? "Success" : "Error",
      message,
      variant: isSuccess ? "success" : "error",
    });
  }

  hideNotification(): void {
    this.modalService.closeDialog();
  }

  closeBulkInvitationResult(): void {
    this.showBulkInvitationResult = false;
    this.bulkInvitationResult = null;
    // If everything succeeded (no failures shown via csvErrors), close the import modal too
    if (this.csvErrors.length === 0) {
      this.closeModals();
    }
  }

  formatDateTime(dateString?: string): string {
    if (!dateString) return "Never";
    // Remove 'Z' suffix if present to prevent UTC conversion
    // DB stores PST time, so we display it as-is
    const cleanedDate = dateString.replace("Z", "");
    return new Date(cleanedDate).toLocaleString("en-US");
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  trackByUserId(index: number, user: User): string {
    return user.id;
  }

  // Pagination methods
  updatePagination(): void {
    this.totalItems = this.filteredUsers.length;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);

    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedUsers = this.filteredUsers.slice(startIndex, endIndex);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  changeItemsPerPage(): void {
    this.currentPage = 1;
    this.updatePagination();
  }

  getVisiblePages(): number[] {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, this.currentPage - delta);
      i <= Math.min(this.totalPages - 1, this.currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (this.currentPage - delta > 2) {
      rangeWithDots.push(1, -1);
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (this.totalPages > 1) {
      if (this.currentPage + delta < this.totalPages - 1) {
        rangeWithDots.push(-1, this.totalPages);
      } else {
        rangeWithDots.push(this.totalPages);
      }
    }

    return rangeWithDots;
  }
}
